import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { GetUsersLookupResponse } from "api/api/usersApi/types";
import { deleteUsers } from "api/thunk/users/deleteAction/deleteUsers";
import { getMeUsers } from "api/thunk/users/getAction/getMeUsers";
import { getUsersByAdmins } from "api/thunk/users/getAction/getUsersByAdmins";
import { getUsersByUserId } from "api/thunk/users/getAction/getUsersByUserId";
import { getUsersWithoutPromocodes } from "api/thunk/users/getAction/getUsersWithoutPromocodes";
import { getDisabledUsers } from "api/thunk/users/getDisabledUsers";
import { getUsers } from "api/thunk/users/getUsers";
import { postUsers } from "api/thunk/users/postAction/postUsers";
import { putMeUsers } from "api/thunk/users/postAction/putMeUsers";
import { putUsersAsAdmin } from "api/thunk/users/postAction/putUsersAsAdmin";
import { putUsersLang } from "api/thunk/users/postAction/putUsersLang";
import { getUsersLookup } from "entities/admin/model/thunk/getUsersLookup";
import { initialState } from "./index";

const usersSlice = createSlice({
  name: "users3",
  initialState,
  reducers: {
    setLookupUsersLoading(state, action: PayloadAction<boolean>) {
      state.usersLookup.response.loading = action.payload;
    },
    setHasMoreLookupUsers(state, action) {
      state.usersLookup.params.hasMore = action.payload;
    },
    setAllLookupUsers(state, action: PayloadAction<GetUsersLookupResponse[]>) {
      state.usersLookup.params.allLookupUsers = [
        ...(state.usersLookup.params.allLookupUsers || []),
        ...action.payload,
      ];
    },
    setLookupUsersPage(state, action: PayloadAction<number>) {
      state.usersLookup.params.page = action.payload;
    },
    setUsersPage(state, action: PayloadAction<number>) {
      state.users.params.page = action.payload;
    },
    setUsersPageSize(state, action: PayloadAction<number>) {
      state.users.params.pageSize = action.payload;
    },
    setDisabledUsersPage(state, action: PayloadAction<number>) {
      state.disabledUsers.params.page = action.payload;
    },
    setDisabledUsersPageSize(state, action: PayloadAction<number>) {
      state.disabledUsers.params.pageSize = action.payload;
    },
    setUsersStatus(state, action: PayloadAction<number | null>) {
      state.users.params.status = action.payload;
    },
    setDisabledUsersStatus(state, action: PayloadAction<number | null>) {
      state.disabledUsers.params.status = action.payload;
    },
    setUsersByAdminsPage(state, action: PayloadAction<number>) {
      state.usersByAdmins.params.page = action.payload;
    },
    setUsersByAdminsPageSize(state, action: PayloadAction<number>) {
      state.usersByAdmins.params.pageSize = action.payload;
    },
    setUsersByAdminsSearchTerm(state, action: PayloadAction<string>) {
      state.usersByAdmins.params.searchTerm = action.payload;
      state.usersByAdmins.params.page = 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.users.response.loading = true;
        state.users.response.error = null;
      })
      .addCase(
        getUsers.fulfilled,
        (state, action) => {
          state.users.response.loading = false;
          state.users.response.data = action.payload;
        }
      )
      .addCase(getUsers.rejected, (state, action) => {
        state.users.response.loading = false;
        state.users.response.error = action.error.message || 'Неизвестная ошибка';
      })
      .addCase(getDisabledUsers.pending, (state) => {
        state.disabledUsers.response.loading = true;
        state.disabledUsers.response.error = null;
      })
      .addCase(
        getDisabledUsers.fulfilled,
        (state, action) => {
          state.disabledUsers.response.loading = false;
          state.disabledUsers.response.data = action.payload;
        }
      )
      .addCase(getDisabledUsers.rejected, (state, action) => {
        state.disabledUsers.response.loading = false;
        state.disabledUsers.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //GET_USER_PROFILE
      .addCase(getMeUsers.pending, (state) => {
        state.meUsers.response.loading = true;
        state.meUsers.response.error = null;
      })
      .addCase(getMeUsers.fulfilled, (state, action) => {
        state.meUsers.response.loading = false;
        state.meUsers.response.data = action.payload;
      })
      .addCase(getMeUsers.rejected, (state, action) => {
        state.meUsers.response.loading = false;
        state.meUsers.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //GET_USERS_LOOKUP
      .addCase(getUsersLookup.pending, (state) => {
        state.usersLookup.response.loading = true;
        state.usersLookup.response.error = null;
      })
      .addCase(getUsersLookup.fulfilled, (state, action) => {
        state.usersLookup.response.loading = false;
        state.usersLookup.response.data = action.payload;
        state.usersLookup.params.allLookupUsers = action.payload.items;
      })
      .addCase(getUsersLookup.rejected, (state, action) => {
        state.usersLookup.response.loading = false;
        state.usersLookup.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //GET_USERS_WITHOUT_PROMOCODES
      .addCase(getUsersWithoutPromocodes.pending, (state) => {
        state.usersWithoutPromocodes.response.loading = true;
        state.usersWithoutPromocodes.response.error = null;
      })
      .addCase(getUsersWithoutPromocodes.fulfilled, (state, action) => {
        state.usersWithoutPromocodes.response.loading = false;
        state.usersWithoutPromocodes.response.data = action.payload;
      })
      .addCase(getUsersWithoutPromocodes.rejected, (state, action) => {
        state.usersWithoutPromocodes.response.loading = false;
        state.usersWithoutPromocodes.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //GET_USERS_BY_USER_ID
      .addCase(getUsersByUserId.pending, (state) => {
        state.userByUserId.response.loading = true;
        state.userByUserId.response.error = null;
      })
      .addCase(getUsersByUserId.fulfilled, (state, action) => {
        state.userByUserId.response.loading = false;
        state.userByUserId.response.data = action.payload;
      })
      .addCase(getUsersByUserId.rejected, (state, action) => {
        state.userByUserId.response.loading = false;
        state.userByUserId.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //GET_USERS_BY_ADMINS
      .addCase(getUsersByAdmins.pending, (state) => {
        state.usersByAdmins.response.loading = true;
        state.usersByAdmins.response.error = null;
      })
      .addCase(
        getUsersByAdmins.fulfilled,
        (state, action) => {
          state.usersByAdmins.response.loading = false;
          state.usersByAdmins.response.data = action.payload;
        }
      )
      .addCase(getUsersByAdmins.rejected, (state, action) => {
        state.usersByAdmins.response.loading = false;
        state.usersByAdmins.response.error = action.error.message || 'Неизвестная ошибка';
      })
      //POST_USERS
      .addCase(postUsers.pending, (state) => {
        state.postUsers.loading = true;
        state.postUsers.error = null;
      })
      .addCase(postUsers.fulfilled, (state, action) => {
        state.postUsers.loading = false;
        state.postUsers.data = action.payload;
      })
      .addCase(postUsers.rejected, (state, action) => {
        state.postUsers.loading = false;
        state.postUsers.error = action.error.message || 'Неизвестная ошибка';
      })
      //PUT_USERS_AS_ADMIN
      .addCase(putUsersAsAdmin.pending, (state) => {
        state.putUsers.loading = true;
        state.putUsers.error = null;
      })
      .addCase(putUsersAsAdmin.fulfilled, (state, action) => {
        state.putUsers.loading = false;
        state.putUsers.data = action.payload;
      })
      .addCase(putUsersAsAdmin.rejected, (state, action) => {
        state.putUsers.loading = false;
        state.putUsers.error = action.error.message || 'Неизвестная ошибка';
      })
      //PUT_ME_USERS
      .addCase(putMeUsers.pending, (state) => {
        state.putMeUsers.loading = true;
        state.putMeUsers.error = null;
      })
      .addCase(putMeUsers.fulfilled, (state, action) => {
        state.putMeUsers.loading = false;
        state.putMeUsers.data = action.payload;
      })
      .addCase(putMeUsers.rejected, (state, action) => {
        state.putMeUsers.loading = false;
        state.putMeUsers.error = action.error.message || 'Неизвестная ошибка';
      })
      //DELETE_USERS
      .addCase(deleteUsers.pending, (state) => {
        state.deleteUsers.loading = true;
        state.deleteUsers.error = null;
      })
      .addCase(deleteUsers.fulfilled, (state, action) => {
        state.deleteUsers.loading = false;
        state.deleteUsers.data = action.payload;
      })
      .addCase(deleteUsers.rejected, (state, action) => {
        state.deleteUsers.loading = false;
        state.deleteUsers.error = action.error.message || 'Неизвестная ошибка';
      })
      //SET_LANGUAGE_USERS
      .addCase(putUsersLang.pending, (state) => {
        state.putUsersLang.loading = true;
        state.putUsersLang.error = null;
      })
      .addCase(putUsersLang.fulfilled, (state, action) => {
        state.putUsersLang.loading = false;
        state.putUsersLang.data = action.payload;
      })
      .addCase(putUsersLang.rejected, (state, action) => {
        state.putUsersLang.loading = false;
        state.putUsersLang.error = action.error.message || 'Неизвестная ошибка';
      })
  },
});

export const {
  setLookupUsersLoading,
  setHasMoreLookupUsers,
  setAllLookupUsers,
  setUsersPage,
  setUsersPageSize,
  setLookupUsersPage,
  setDisabledUsersPage,
  setDisabledUsersPageSize,
  setUsersByAdminsPage,
  setUsersByAdminsPageSize,
  setUsersStatus,
  setDisabledUsersStatus,
  setUsersByAdminsSearchTerm
} = usersSlice.actions;
export default usersSlice.reducer;
